import { CSSTransition } from 'react-transition-group';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { createContext, Component } from 'react';

import navigationData from './data/navigation';
import Feedback from '@xingternal/malt-feedback';

import { LOGIN_STATE } from './constants';
import styles from './XingFrameProvider.css';

const { Provider, Consumer } = createContext({});
const omViewAnimationDuration = 200;

const nop = () => {};
const isInteger = n => (n ^ 0) === +n;

class XingFrameProvider extends Component {
  static propTypes = {
    acknowledgeNotification: PropTypes.func,
    activeItem: PropTypes.oneOf([
      'activities',
      'contactRequests',
      'events',
      'groupsWeb',
      'jobs',
      'news',
      'notificationCenterWeb',
      'probusinessNotifications',
      'unreadChats',
      'search',
    ]),
    appStats: PropTypes.object,
    changeNotification: PropTypes.func,
    children: PropTypes.node,
    executeSearch: PropTypes.func,
    executeSearchTracking: PropTypes.func,
    features: PropTypes.object,
    featureSwitches: PropTypes.arrayOf(PropTypes.string),
    fetchViewer: PropTypes.func,
    loadingMainQuery: PropTypes.bool,
    loggedOut: PropTypes.bool,
    loginState: PropTypes.oneOf(Object.values(LOGIN_STATE)),
    notifications: PropTypes.object,
    refreshNotifications: PropTypes.func,
    resetSearch: PropTypes.func,
    searchSuggestions: PropTypes.array,
    type: PropTypes.string,
    user: PropTypes.object,
  };

  static defaultProps = {
    acknowledgeNotification: nop,
    activeItem: null,
    changeNotification: nop,
    executeSearch: nop,
    executeSearchTracking: nop,
    featureSwitches: [],
    loadingMainQuery: false,
    loginState: LOGIN_STATE.softLoggedIn,
    refreshNotifications: nop,
    resetSearch: nop,
  };

  state = {
    hasActiveSearchBar: false,
    hasOpenedSidebar: false,
    isOmViewMessageVisible: false,
    isOmViewTransitionDone: false,
    isOmViewVisible: false,
    mounted: false,
    offsetTop: 0,
    omViewMessageContent: undefined,
    omViewMessageTimeout: 0,
    omViewMessageType: undefined,
    searchTerm: '',
  };

  messageTimeoutId;
  omViewRef = React.createRef();

  activateSearchBar = () => {
    this.setState({ hasActiveSearchBar: true });
    if (this.state.searchTerm) {
      this.props.executeSearch(this.state.searchTerm);
      this.setState({ hasActiveSearchBar: true });
    }
  };
  deactivateSearchBar = () => {
    this.setState({ hasActiveSearchBar: false });
    this.props.resetSearch();
  };

  activateOmView = () => {
    this.setState({ isOmViewVisible: true, isOmViewMessageVisible: false });
  };

  deactivateOmView = () => {
    this.setState({ isOmViewVisible: false });
  };

  setOmViewMessage = (
    { type = 'confirmation', content, timeout, action },
    show = false
  ) => {
    this.setState({
      omViewMessageContent: content,
      omViewMessageTimeout: timeout,
      omViewMessageType: type,
      omViewMessageAction: action,
      isOmViewMessageVisible: show,
    });
  };

  handleOmViewMessageShow = () => {
    if (this.state.omViewMessageContent) {
      this.setState({ isOmViewMessageVisible: true });
    }

    if (this.messageTimeoutId) {
      clearTimeout(this.messageTimeoutId);
    }

    if (this.state.omViewMessageTimeout) {
      this.messageTimeoutId = setTimeout(
        this.handleOmViewMessageClose,
        this.state.omViewMessageTimeout
      );
    }
  };

  handleOmViewMessageClose = () => {
    this.setState({ isOmViewMessageVisible: false });
  };

  triggerSearchInput = searchTerm => {
    this.setState({
      searchTerm,
    });

    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }

    if (searchTerm === '') {
      return this.props.resetSearch();
    }

    this.timerId = setTimeout(() => {
      this.props.executeSearch(searchTerm);
    }, 300);
  };

  componentDidMount() {
    const { fetchViewer, resetSearch } = this.props;

    // fetchViewer is only set by withReactData or withApolloData (logged in cases) - withStaticData (logged out case) does not
    if (fetchViewer) {
      fetchViewer();
    }

    // in hot reloading cases, the store could still be filled
    resetSearch();
  }

  toggleSidebar = () => {
    const offsetTop = window && window.pageYOffset;
    this.setState(state => {
      const stateChange = { hasOpenedSidebar: !state.hasOpenedSidebar };
      if (!state.hasOpenedSidebar) {
        stateChange.offsetTop = offsetTop;
      }
      return stateChange;
    });
  };

  getActiveItem = () => this.props.activeItem || 'notificationCenterWeb';

  acknowledgeCurrentNotification = () =>
    this.props.acknowledgeNotification(this.getActiveItem());

  changeCurrentNotification = (section, count) => {
    // Backwards compatible
    if (typeof section === 'string') {
      this.props.changeNotification(section, count);
    } else if (isInteger(section)) {
      this.props.changeNotification(this.getActiveItem(), section);
    }
  };

  componentDidUpdate(_, prevState) {
    prevState.hasOpenedSidebar &&
      !this.state.hasOpenedSidebar &&
      global.scrollTo(0, this.state.offsetTop);
  }

  render() {
    const {
      activeItem,
      loginState,
      executeSearchTracking,
      features,
      featureSwitches,
      loadingMainQuery,
      loggedOut,
      notifications,
      refreshNotifications,
      searchSuggestions,
      type,
      user,
    } = this.props;

    if (!type) {
      throw new Error(
        'No data source provided. Please use the Higher Order Component `withStaticData`, `withApolloData` or `withReduxData` based on your project setup'
      );
    }
    const hasSearchSuggestions =
      Array.isArray(searchSuggestions) &&
      !!searchSuggestions.find(section => section.suggestions.length > 0);

    const xingId = {
      ...user,
      // If user.id is available
      ...(user &&
        user.id && {
          get unscrambledId() {
            // if user.id is a number, return it. Otherwise, parse and return the int part of it
            return typeof user.id === 'string'
              ? parseInt(user.id.split('.')[0])
              : user.id;
          },
        }),
    };

    return (
      <React.Fragment>
        <Provider
          value={{
            ...this.state,
            acknowledgeNotification: this.acknowledgeCurrentNotification,
            activateOmView: this.activateOmView,
            activateSearchBar: this.activateSearchBar,
            activeItem: activeItem || this.getActiveItem(),
            changeNotification: this.changeCurrentNotification,
            deactivateOmView: this.deactivateOmView,
            deactivateSearchBar: this.deactivateSearchBar,
            executeSearchTracking,
            features,
            featureSwitches,
            handleOmViewMessageClose: this.handleOmViewMessageClose,
            handleOmViewMessageShow: this.handleOmViewMessageShow,
            hasUser: Object.keys(user || {}).length > 0,
            hasSearchSuggestions,
            isOmViewMessageVisible: this.state.isOmViewVisible,
            navigationData,
            loadingMainQuery,
            loggedOut: loggedOut === true,
            loginState,
            notifications,
            omViewAnimationDuration,
            omViewRef: this.omViewRef,
            refreshNotifications,
            searchSuggestions,
            setOmViewMessage: this.setOmViewMessage,
            toggleSidebar: this.toggleSidebar,
            triggerSearchInput: this.triggerSearchInput,
            user: xingId,
          }}
        >
          <CSSTransition
            in={this.state.isOmViewVisible}
            classNames={{
              enter: styles.omViewEnter,
              enterActive: styles.omViewEnterActive,
              enterDone: styles.omViewEnterDone,
              exit: styles.omViewExit,
              exitActive: this.state.omViewMessageContent
                ? styles.omViewExitToTopActive
                : styles.omViewExitActive,
              exitDone: styles.omViewExitDone,
            }}
            timeout={omViewAnimationDuration}
            onExit={this.handleOmViewMessageShow}
          >
            <div
              className={classNames(styles.omView, {})}
              ref={this.omViewRef}
            />
          </CSSTransition>
          <CSSTransition
            in={!this.state.isOmViewVisible}
            classNames={{
              enter: styles.contentEnter,
              enterActive: styles.contentEnterActive,
              enterDone: styles.contentEnterDone,
              exit: styles.contentExit,
              exitActive: styles.contentExitActive,
              exitDone: styles.contentExitDone,
            }}
            timeout={omViewAnimationDuration}
          >
            <div>{this.props.children}</div>
          </CSSTransition>
          <Feedback
            action={this.state.omViewMessageAction}
            onCancel={this.handleOmViewMessageClose}
            type={this.state.omViewMessageType}
            show={this.state.isOmViewMessageVisible}
          >
            {this.state.omViewMessageContent}
          </Feedback>
        </Provider>
      </React.Fragment>
    );
  }
}

export { XingFrameProvider, Consumer as XingFrameConsumer };
